import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { Alert, StyleSheet, View } from 'react-native';
import { format, isAfter, isBefore, isFuture, isValid, parseISO, } from 'date-fns';
import { AppView, rem } from 'design-system';
import DateInput from 'design-system/date/DateInput';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import StatusBar from 'design-system/StatusBar';
import useColors from 'hooks/useColors';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
const strings = {
    moveInTitle: 'When did you move in?',
    moveOutTitle: 'When did you move out?',
    subTitle: 'An estimate is fine if you don’t remember the exact date',
    buttonNext: 'Continue',
};
const toDateState = (date) => {
    if (!date)
        return {};
    const parsed = parseISO(date);
    if (!isValid(parsed))
        return {};
    return {
        day: format(parsed, 'd'),
        month: format(parsed, 'M'),
        year: format(parsed, 'yyyy'),
    };
};
const AddressDateScreen = ({ navigation, route, }) => {
    const colors = useColors();
    const { mode, initialDate, articlesKey, onSave, startDate, endDate } = route.params;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            headerRight: () => <HelpScreenButton articlesKey={articlesKey}/>,
        });
    }, [articlesKey, colors.text.primary]);
    const [date, setDate] = useState(() => toDateState(initialDate));
    const parsedDate = useMemo(() => {
        const { day, month, year } = date;
        if (!Number(day) || !Number(month) || !year || year.length < 4) {
            return undefined;
        }
        const value = new Date(Number(year), Number(month) - 1, Number(day));
        if (!isValid(value) ||
            value.getDate() !== Number(day) ||
            Number(year) < 1900) {
            return undefined;
        }
        return value;
    }, [date]);
    const onPressContinue = useCallback(() => {
        if (!parsedDate)
            return;
        if (isFuture(parsedDate)) {
            Alert.alert('Hold on 💫', 'You can’t set a date in the future.');
            return;
        }
        const bound = mode === 'moveIn' ? endDate : startDate;
        const boundDate = bound ? parseISO(bound) : undefined;
        if (boundDate && isValid(boundDate)) {
            const isOutOfOrder = mode === 'moveIn'
                ? isAfter(parsedDate, boundDate)
                : isBefore(parsedDate, boundDate);
            if (isOutOfOrder) {
                Alert.alert('Hold on 💫', mode === 'moveIn'
                    ? 'Your move in date cannot be after your move out date.'
                    : 'Your move out date cannot be before your move in date.');
                return;
            }
        }
        onSave(parsedDate);
    }, [endDate, mode, onSave, parsedDate, startDate]);
    return (<AppView avoidHeader>
      <StatusBar barStyle="dark-content" animated/>
      <KeyboardAvoidingContainerNew flexContainer withButtonSpace withMarginHorizontal={false}>
        <ScreenInfoHeader title={mode === 'moveOut' ? strings.moveOutTitle : strings.moveInTitle} subTitle={strings.subTitle}/>
        <View style={styles.inputContainer}>
          <DateInput state={date} onDark onChange={setDate} onSubmitEditing={onPressContinue}/>
        </View>
      </KeyboardAvoidingContainerNew>
      <KeyboardAvoidingButton brand withFrame={false} disabled={!parsedDate} title={strings.buttonNext} onPress={onPressContinue}/>
    </AppView>);
};
const styles = StyleSheet.create({
    inputContainer: {
        flex: 1,
        justifyContent: 'center',
        paddingBottom: rem(24),
    },
});
export default AddressDateScreen;
